import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLocale } from '../i18n/LocaleProvider';
import { pick, compareJobs } from '../i18n/pick';
import { listJobs } from '../api/jobs';
import { orgLogo, orgAccent } from '../lib/media';
import Header from '../components/Header';
import Footer from '../components/Footer';
import JobCard from '../components/JobCard';
import Seo from '../components/Seo';
import SpontaneousApplication from '../components/SpontaneousApplication';
import { Loading, ErrorState, Empty } from '../components/States';
import { tenant } from '../tenant';

function groupByOrg(jobs) {
  const groups = [];
  const bySlug = new Map();
  for (const job of jobs) {
    const org = job.organization || { slug: '__none__', name: '' };
    if (!bySlug.has(org.slug)) {
      const g = { org, jobs: [] };
      bySlug.set(org.slug, g);
      groups.push(g);
    }
    bySlug.get(org.slug).jobs.push(job);
  }
  groups.forEach((g) => g.jobs.sort(compareJobs));
  // Float the active brand's block to the top (per-domain); stable sort keeps
  // the rest in their original order.
  if (tenant.primaryOrg) {
    groups.sort((a, b) =>
      (a.org.slug === tenant.primaryOrg ? 0 : 1) -
      (b.org.slug === tenant.primaryOrg ? 0 : 1)
    );
  }
  return groups;
}

function OrgGroup({ org, jobs }) {
  const { locale } = useLocale();
  const logo = orgLogo(org);
  const tagline = pick(org, locale, 'tagline');
  const accent = orgAccent(org.slug);

  return (
    <section className="scroll-mt-20" id={`org-${org.slug}`}>
      <div className="mb-5 flex items-center gap-4">
        <span className="h-9 w-1 rounded-full" style={{ backgroundColor: accent }} />
        {logo ? (
          <img
            src={logo}
            alt={org.name}
            className="h-11 w-11 rounded-lg border border-sand bg-white object-contain p-1"
            loading="lazy"
          />
        ) : null}
        <div>
          <h2 className="font-display text-2xl font-semibold leading-tight text-greenDeep">
            {org.name}
          </h2>
          {tagline && <p className="text-sm text-muted-foreground">{tagline}</p>}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {jobs.map((job) => (
          <JobCard key={job.id ?? job.slug} job={job} />
        ))}
      </div>
    </section>
  );
}

export default function JobsList() {
  const { t, locale } = useLocale();
  const [searchParams] = useSearchParams();
  const orgFilter = searchParams.get('org') || undefined;
  const [status, setStatus] = useState('loading'); // loading | error | ready
  const [jobs, setJobs] = useState([]);

  const load = useCallback(() => {
    setStatus('loading');
    listJobs({ organization: orgFilter })
      .then((data) => {
        setJobs(data);
        setStatus('ready');
      })
      .catch((e) => {
        console.error('Failed to load jobs:', e);
        setStatus('error');
      });
  }, [orgFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(() => groupByOrg(jobs), [jobs]);

  return (
    <div className="flex min-h-screen flex-col">
      <Seo title={t.metaTitleHome} description={t.metaDescHome} locale={locale} />
      <Header />

      <main className="flex-1">
        {/* Hero */}
        <div className="border-b border-sand/70 bg-gradient-to-b from-cream/60 to-transparent">
          <div className="container py-8 sm:py-10">
            <p className="eyebrow">{t.heroEyebrow}</p>
            <h1 className="mt-2 max-w-3xl font-display text-3xl font-semibold leading-[1.1] text-greenDeep sm:text-4xl">
              {t.heroTitle}
            </h1>
            <p className="mt-3 max-w-xl text-base text-ink/75 sm:text-lg">{t.heroSubtitle}</p>
            {status === 'ready' && jobs.length > 0 && (
              <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 font-num text-xs uppercase tracking-wider text-[#8a6418]">
                {t.openingsCount(jobs.length)}
              </p>
            )}
          </div>
        </div>

        <div className="container py-8 sm:py-10">
          {status === 'loading' && <Loading />}
          {status === 'error' && <ErrorState onRetry={load} />}
          {status === 'ready' && (
            <div className="space-y-12">
              {jobs.length === 0 ? (
                <Empty />
              ) : (
                groups.map((g) => (
                  <OrgGroup key={g.org.slug} org={g.org} jobs={g.jobs} />
                ))
              )}
              <SpontaneousApplication />
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
